/**
 * Plan 14a, Task 3.1 — Three stock particle presets: hit-impact, jump-dust, death-dissolve.
 *
 * Design:
 *   - Each preset is a const object satisfying the `ParticlePreset` interface from types.ts.
 *   - `spawn()` is a factory; each call returns a fresh THREE.Group containing a
 *     three.quarks ParticleEmitter as its first child. The Group is the "emitter handle"
 *     threaded through update/isFinished/reset.
 *   - Elapsed time is tracked in `emitter.userData._quarksElapsedMs`. This allows
 *     `isFinished()` to answer correctly in tests (no parent Scene / BatchedRenderer required).
 *   - In production the VFX manager (Task 5.1) adds the group to the scene and may also
 *     call system.update() via a BatchedRenderer, but the preset API works independently.
 *
 * three.quarks API confirmed from local .d.ts files:
 *   node_modules/three.quarks/dist/types/ParticleSystem.d.ts
 *     - `ParticleSystemParameters.material` is required; all others optional.
 *     - `ParticleSystemParameters.duration` (seconds), `looping`, `shape`, `startLife`,
 *       `startSpeed`, `startSize`, `startColor`, `emissionOverTime`, `behaviors`, `autoDestroy`.
 *     - `system.emitter` is a `ParticleEmitter extends Object3D`.
 *   node_modules/three.quarks/dist/types/shape/SphereEmitter.d.ts — sphere emit shape
 *   node_modules/three.quarks/dist/types/shape/PointEmitter.d.ts — point emit shape
 *   node_modules/three.quarks/dist/types/functions/ConstantValue.d.ts — constant numeric generator
 *   node_modules/three.quarks/dist/types/functions/IntervalValue.d.ts — random in [a, b]
 *   node_modules/three.quarks/dist/types/functions/ColorRange.d.ts — lerp two RGBA Vector4 values
 *   node_modules/three.quarks/dist/types/behaviors/ApplyForce.d.ts — constant force behavior
 *   node_modules/three.quarks/dist/types/VFXBatch.d.ts — RenderMode enum
 */

import * as THREE from 'three';
import {
  ParticleSystem,
  ConstantValue,
  IntervalValue,
  ColorRange,
  SphereEmitter,
  PointEmitter,
  ApplyForce,
  RenderMode,
} from 'three.quarks';
import type { ParticlePreset, ParticlePresetId } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Key for the elapsed-ms tracker stored on emitter.userData. */
const ELAPSED_KEY = '_quarksElapsedMs';

/** Retrieve the elapsed timer stored on the emitter object (default 0). */
function getElapsed(emitter: THREE.Object3D): number {
  const v = emitter.userData[ELAPSED_KEY];
  return typeof v === 'number' ? v : 0;
}

function setElapsed(emitter: THREE.Object3D, ms: number): void {
  emitter.userData[ELAPSED_KEY] = ms;
}

/** MeshBasicMaterial with transparency — no WebGL calls at construction time. */
function makeBasicMaterial(
  color: THREE.ColorRepresentation,
  opacity: number,
): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({ color, transparent: true, opacity });
}

// ─────────────────────────────────────────────────────────────────────────────
// Preset factory
// ─────────────────────────────────────────────────────────────────────────────

interface PresetConfig {
  readonly id: ParticlePresetId;
  readonly maxActive: number;
  /** Lifetime in seconds. isFinished triggers when elapsed >= lifetimeSec. */
  readonly lifetimeSec: number;
  makeSystem(): ParticleSystem;
}

function makePreset(cfg: PresetConfig): ParticlePreset {
  return {
    id: cfg.id,
    maxActive: cfg.maxActive,

    spawn(): THREE.Object3D {
      const system = cfg.makeSystem();
      const group = new THREE.Group();
      // ParticleEmitter extends Object3D; adding it as a child satisfies the
      // "at least one child" contract required by the manager and tests.
      group.add(system.emitter);
      setElapsed(group, 0);
      return group;
    },

    update(emitter: THREE.Object3D, deltaSeconds: number): void {
      setElapsed(emitter, getElapsed(emitter) + deltaSeconds * 1000);
    },

    isFinished(emitter: THREE.Object3D): boolean {
      return getElapsed(emitter) >= cfg.lifetimeSec * 1000;
    },

    reset(emitter: THREE.Object3D): void {
      setElapsed(emitter, 0);
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. hit-impact — maxActive: 12, lifetime: 400ms
//    Bright yellow/orange burst, outward sphere spread.
// ─────────────────────────────────────────────────────────────────────────────

export const hitImpactPreset: ParticlePreset = makePreset({
  id: 'hit-impact',
  maxActive: 12,
  lifetimeSec: 0.4,
  makeSystem() {
    const mat = makeBasicMaterial(0xffdd33, 0.9);
    return new ParticleSystem({
      duration: 0.4,
      looping: false,
      autoDestroy: false,
      shape: new SphereEmitter({ radius: 0.15, thickness: 1 }),
      startLife: new ConstantValue(0.35),
      startSpeed: new IntervalValue(2.0, 4.0),
      startSize: new IntervalValue(0.08, 0.18),
      startColor: new ColorRange(
        new THREE.Vector4(1.0, 0.9, 0.2, 1.0),
        new THREE.Vector4(1.0, 0.4, 0.0, 1.0),
      ),
      emissionOverTime: new ConstantValue(0),
      emissionBursts: [
        { time: 0, count: new ConstantValue(12), cycle: 1, interval: 0.1, probability: 1 },
      ],
      behaviors: [],
      renderMode: RenderMode.BillBoard,
      material: mat,
      worldSpace: true,
    });
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// 2. jump-dust — maxActive: 8, lifetime: 600ms
//    Dusty tan puff that drifts upward.
// ─────────────────────────────────────────────────────────────────────────────

export const jumpDustPreset: ParticlePreset = makePreset({
  id: 'jump-dust',
  maxActive: 8,
  lifetimeSec: 0.6,
  makeSystem() {
    const mat = makeBasicMaterial(0xd4b896, 0.7);
    return new ParticleSystem({
      duration: 0.4,
      looping: false,
      autoDestroy: false,
      shape: new PointEmitter(),
      startLife: new ConstantValue(0.55),
      startSpeed: new IntervalValue(0.5, 1.5),
      startSize: new IntervalValue(0.12, 0.24),
      startColor: new ColorRange(
        new THREE.Vector4(0.83, 0.72, 0.59, 0.8),
        new THREE.Vector4(0.7, 0.65, 0.55, 0.6),
      ),
      emissionOverTime: new ConstantValue(0),
      emissionBursts: [
        { time: 0, count: new ConstantValue(8), cycle: 1, interval: 0.1, probability: 1 },
      ],
      behaviors: [new ApplyForce(new THREE.Vector3(0, 1.5, 0), new ConstantValue(1))],
      renderMode: RenderMode.BillBoard,
      material: mat,
      worldSpace: true,
    });
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// 3. death-dissolve — maxActive: 4, lifetime: 1200ms
//    Dark smoke/ember spiral that drifts upward.
// ─────────────────────────────────────────────────────────────────────────────

export const deathDissolvePreset: ParticlePreset = makePreset({
  id: 'death-dissolve',
  maxActive: 4,
  lifetimeSec: 1.2,
  makeSystem() {
    const mat = makeBasicMaterial(0x222222, 0.8);
    return new ParticleSystem({
      duration: 0.8,
      looping: false,
      autoDestroy: false,
      shape: new SphereEmitter({ radius: 0.3, thickness: 1 }),
      startLife: new ConstantValue(1.1),
      startSpeed: new IntervalValue(0.3, 0.8),
      startSize: new IntervalValue(0.1, 0.3),
      startColor: new ColorRange(
        new THREE.Vector4(0.2, 0.2, 0.2, 0.9),
        new THREE.Vector4(0.6, 0.3, 0.0, 0.5),
      ),
      emissionOverTime: new ConstantValue(0),
      emissionBursts: [
        { time: 0, count: new ConstantValue(4), cycle: 1, interval: 0.1, probability: 1 },
      ],
      behaviors: [new ApplyForce(new THREE.Vector3(0, 0.8, 0), new ConstantValue(1))],
      renderMode: RenderMode.BillBoard,
      material: mat,
      worldSpace: true,
    });
  },
});
